'use client';

import { useEffect, useState } from 'react';
import { GmAwardsButton } from '@/components/entities/gamification/GmAwardsButton';
import { GmLevelProgress } from '@/components/entities/gamification/GmLevelProgress';
import { cn } from '@/utils/cn';
import { useGmBarPlayerData } from '../../../model/hooks/useGmBarPlayerData';
import { GmStatusBlock } from '../GmBarMobile/components/GmStatusBlock';

interface GmBarProgressHeaderProps {
  /** Additional class names for the header root container. */
  className?: string;
  /** Number shown in the awards button, usually the amount of available rewards. */
  awardsCount?: number;
  /** Called when the awards button is clicked. */
  onAwardsClick?: VoidFunction;
  /** Controls the mobile status row reveal animation progress, from 0 hidden to 1 visible. */
  statusRevealProgress?: number;
  /** Whether to render the status row below the level progress and awards controls. */
  shouldRenderStatusRow?: boolean;
  /** Visual state of the awards button, based on reward availability or game center state. */
  awardsState?: 'rewardAvailable' | 'gameCenterOpen';
}

/**
 * Renders the gamification bar progress header with the player's level progress,
 * awards action, and optional current status row.
 *
 * @param props Header configuration and callbacks.
 * @param props.onAwardsClick Handles clicks on the awards button.
 * @param props.className Additional class names for the header root container.
 * @param props.awardsCount Number shown in the awards button.
 * @param props.awardsState Visual state for the awards button.
 * @param props.statusRevealProgress Mobile status row reveal animation progress.
 * @param props.shouldRenderStatusRow Whether to render the current status row.
 * @returns The rendered progress header component.
 */
export const GmBarProgressHeader = ({
  onAwardsClick,
  className,
  awardsCount,
  awardsState,
  statusRevealProgress = 1,
  shouldRenderStatusRow = true,
}: GmBarProgressHeaderProps) => {
  const { progress, playerProfile, isLoading } = useGmBarPlayerData();
  const [hasHydrated, setHasHydrated] = useState(false);
  const showLoadingState = !hasHydrated || isLoading;
  const visibleProgress = hasHydrated ? progress : null;
  const visiblePlayerProfile = hasHydrated ? playerProfile : null;

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  const currentLevelXp = (visibleProgress?.totalXp ?? 0) - (visibleProgress?.currentLevelXpRequired ?? 0);
  const levelXpRequired = (visibleProgress?.nextLevelXpRequired ?? 0) - (visibleProgress?.currentLevelXpRequired ?? 0);
  const currentStatus = visiblePlayerProfile?.progressStatus ?? null;

  return (
    <div className={className}>
      <div className={cn('flex gap-4.5 px-4 py-2.5', shouldRenderStatusRow && 'pb-3 pt-4')}>
        <GmLevelProgress
          loading={showLoadingState}
          appearance="mobileBar"
          className="min-w-0 flex-1"
          currentValue={currentLevelXp}
          levelNumber={visibleProgress?.level || 0}
          progressPercent={visibleProgress?.percent || 0}
          targetValue={levelXpRequired}
        />

        <GmAwardsButton
          state={awardsState}
          onClick={onAwardsClick}
          loading={showLoadingState}
        >
          {awardsCount}
        </GmAwardsButton>
      </div>

      {shouldRenderStatusRow && (
        <GmStatusBlock
          loading={showLoadingState}
          currentStatus={currentStatus}
          statusRevealProgress={statusRevealProgress}
        />
      )}
    </div>
  );
};
